import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type PropsWithChildren,
} from 'react'
import type { Session, User } from '@supabase/supabase-js'
import {
  claimPendingOrganisationInvitations,
  loadMyPlatformCapabilities,
  loadUserProfile,
  type UserProfile,
} from '../lib/governance-api'
import { getSupabaseBrowserClient } from '../lib/supabase'

type AssuranceLevel = 'aal1' | 'aal2' | null

type AuthContextValue = {
  session: Session | null
  user: User | null
  profile: UserProfile | null
  platformCapabilities: string[]
  assuranceLevel: AssuranceLevel
  loading: boolean
  error: string | null
  refreshIdentity: () => Promise<void>
  signOut: () => Promise<void>
}

const AuthContext = createContext<AuthContextValue | null>(null)

export function AuthProvider({ children }: PropsWithChildren) {
  const [session, setSession] = useState<Session | null>(null)
  const [profile, setProfile] = useState<UserProfile | null>(null)
  const [platformCapabilities, setPlatformCapabilities] = useState<string[]>([])
  const [assuranceLevel, setAssuranceLevel] = useState<AssuranceLevel>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const refreshIdentityForSession = useCallback(async (currentSession: Session | null) => {
    if (!currentSession?.user) {
      setProfile(null)
      setPlatformCapabilities([])
      setAssuranceLevel(null)
      return
    }

    const supabase = getSupabaseBrowserClient()

    try {
      // Invitation claiming is useful during first access, but it is not part of
      // the core identity read. A transient claim failure must not hide an
      // existing profile or make the app treat an established account as new.
      const invitationClaim = claimPendingOrganisationInvitations().catch(() => null)

      const [nextProfile, capabilities, assurance] = await Promise.all([
        loadUserProfile(currentSession.user.id),
        loadMyPlatformCapabilities(),
        supabase.auth.mfa.getAuthenticatorAssuranceLevel(),
      ])

      await invitationClaim

      const currentLevel = assurance.data?.currentLevel

      setProfile(nextProfile)
      setPlatformCapabilities(capabilities)
      setAssuranceLevel(
        currentLevel === 'aal2' ? 'aal2' : currentLevel === 'aal1' ? 'aal1' : null,
      )
      setError(null)
    } catch (identityError) {
      setError(identityError instanceof Error ? identityError.message : 'Unable to load user access.')
    }
  }, [])

  const refreshIdentity = useCallback(async () => {
    await refreshIdentityForSession(session)
  }, [refreshIdentityForSession, session])

  useEffect(() => {
    const supabase = getSupabaseBrowserClient()
    let active = true

    void supabase.auth.getSession().then(async ({ data, error: sessionError }) => {
      if (!active) {
        return
      }

      if (sessionError) {
        setError(sessionError.message)
        setLoading(false)
        return
      }

      setSession(data.session)
      await refreshIdentityForSession(data.session)

      if (active) {
        setLoading(false)
      }
    })

    const { data: authListener } = supabase.auth.onAuthStateChange((_event, nextSession) => {
      setLoading(true)
      setSession(nextSession)

      // Defer Supabase calls until after the auth callback completes to avoid
      // re-entering the auth lock held by the client library.
      window.setTimeout(() => {
        if (!active) return
        void refreshIdentityForSession(nextSession).finally(() => {
          if (active) setLoading(false)
        })
      }, 0)
    })

    return () => {
      active = false
      authListener.subscription.unsubscribe()
    }
  }, [refreshIdentityForSession])

  const signOut = useCallback(async () => {
    const supabase = getSupabaseBrowserClient()
    const { error: signOutError } = await supabase.auth.signOut()

    if (signOutError) {
      throw signOutError
    }
  }, [])

  const value = useMemo<AuthContextValue>(
    () => ({
      session,
      user: session?.user ?? null,
      profile,
      platformCapabilities,
      assuranceLevel,
      loading,
      error,
      refreshIdentity,
      signOut,
    }),
    [
      session,
      profile,
      platformCapabilities,
      assuranceLevel,
      loading,
      error,
      refreshIdentity,
      signOut,
    ],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth() {
  const context = useContext(AuthContext)

  if (!context) {
    throw new Error('useAuth must be used within AuthProvider')
  }

  return context
}
